/**
 * L'ÉDITEUR DE FIGURE, EN MODE AUTEUR — atelier de figures, lot L7 (AB#943 ;
 * `docs/pilotage/atelier-figures/CONCEPTION.md` § 5.3).
 *
 * « Les agents écrivent les figures à la main dans les fichiers du chapitre,
 * sans les voir. » Ici, l'auteur CONSTRUIT : la figure de l'énoncé (ses points
 * nommés, son repère), puis — pour un tracé — la solution, et l'éditeur
 * PROPOSE les contraintes qu'elle satisfait ; il construit les tracés fautifs
 * et rattache chacun à une erreur type ; pour une figure à observer, il
 * désigne les points DÉPLAÇABLES et les mesures qui suivent (L2, F2) ; pour
 * une courbe, les paramètres RÉGLABLES aux curseurs (L2, F1). La description
 * est proposée par `decrireFigureV2`.
 *
 * Ce module est la partie PURE de l'éditeur, partagée pour qu'un test de
 * l'import (l'API) éprouve EXACTEMENT ce que la console produit :
 *
 *  - `figureDEnonceDuProjet` : la construction de l'auteur → la figure v2 du
 *    contrat d'entrée (points par leur nom) ;
 *  - `proposerContraintes` : chaque contrainte du vocabulaire que l'objet
 *    désigné comme cible TIENT, mesurée par le vérificateur du produit ;
 *  - `exerciceDuProjet` : l'exercice au format d'échange du chapitre, et ses
 *    GRIEFS en direct (I-8 : une figure intraçable est refusée à l'écriture —
 *    tant qu'il y en a un, l'éditeur ne livre rien).
 *
 * La SORTIE est le JSON d'un exercice, à verser dans `exercices/*.json` du
 * chapitre ; l'écriture en base reste la chaîne d'import existante (validation,
 * vérification par calcul, import). Rien ici n'écrit ni n'appelle un modèle
 * (I-11).
 */
import { attenduDuTrace, documentDeTrace, CATEGORIES_EXERCICE, COMPETENCES_EXERCICE, OUTILS_TRACE, } from '../types/manuel-exercices.js';
import { FORMAT_CHAPITRE_VERSION } from '../types/manuel-programme.js';
import { griefsDocument, resoudreConstruction, } from './construction.js';
import { phrasesDesContraintes } from './contraintes-en-phrases.js';
import { decrireFigureV2 } from './decrire-v2.js';
import { griefsFigureDynamique } from './figure-dynamique.js';
import { normaliserFigureV2, } from './figure-v2.js';
import { jugerTrace } from './juger-trace.js';
import { griefsDuReglage } from './reglage.js';
import { OUTIL_PAR_DEFINITION, verifierConstruction } from './verifier.js';
/* ─────────────────────────── la figure d'énoncé ─────────────────────────── */
/** Un nombre écrit propre : 4 décimales au plus, le bruit flottant retiré. */
export function nombrePropre(v) {
    const r = Math.round(v * 1e4) / 1e4;
    return Object.is(r, -0) ? 0 : r;
}
/**
 * La construction de l'auteur, en figure v2 du contrat d'entrée : les points
 * par leur nom, les segments, droites, polygones, cercles et vecteurs par les
 * noms de leurs points. Un objet que la figure d'énoncé n'écrit pas (une
 * médiatrice, une intersection) est repris par ce qu'il EST (ses coordonnées) ;
 * ce qui ne se reprend pas du tout est dit dans `griefs`.
 */
export function figureDEnonceDuProjet(repere, objets) {
    const griefs = [];
    const doc = documentDeTrace(repere, { objets, calque: false });
    const resolue = resoudreConstruction(doc, null);
    const parId = new Map(objets.map((o) => [o.id, o]));
    const coord = (p) => ({ x: nombrePropre(p.x), y: nombrePropre(p.y) });
    /** Un point cité : son nom s'il est un point nommé de la figure, ses coordonnées sinon. */
    const ref = (id) => {
        const o = parId.get(id);
        const r = resolue.objets.get(id);
        if (r?.genre !== 'point')
            return null;
        return o?.nom !== undefined && o.nom.trim() !== '' ? o.nom.trim() : coord(r.p);
    };
    const sortie = [];
    for (const o of objets) {
        const r = resolue.objets.get(o.id);
        if (o.masque === true || r === undefined || r.genre === 'indefini')
            continue;
        const d = o.def;
        switch (r.genre) {
            case 'point': {
                const nom = o.nom?.trim();
                if (nom === undefined || nom === '') {
                    griefs.push(`un point de la figure n’a pas de nom (${coord(r.p).x} ; ${coord(r.p).y})`);
                    break;
                }
                sortie.push({ type: 'point', nom, ...coord(r.p) });
                break;
            }
            case 'ligne': {
                const de = typeof d['de'] === 'string' ? ref(d['de']) : null;
                const a = typeof d['a'] === 'string' ? ref(d['a']) : null;
                const sorte = r.sorte === 'segment' ? 'segment' : 'droite';
                if (r.sorte === 'demi_droite') {
                    griefs.push('une demi-droite ne s’écrit pas dans une figure d’énoncé');
                    break;
                }
                sortie.push((o.def.type === 'segment' || o.def.type === 'droite') && de !== null && a !== null
                    ? { type: sorte, de, a }
                    : { type: sorte, de: coord(r.a), a: coord(r.b) });
                break;
            }
            case 'cercle': {
                const c = o.def.type === 'cercle_par_point' || o.def.type === 'cercle_rayon'
                    ? ref(o.def.centre)
                    : null;
                sortie.push({ type: 'cercle', centre: c ?? coord(r.centre), rayon: nombrePropre(r.rayon) });
                break;
            }
            case 'polygone': {
                const sommets = o.def.type === 'polygone' ? o.def.sommets.map((s) => ref(s)) : r.sommets.map(coord);
                sortie.push({
                    type: 'polygone',
                    sommets: sommets.every((s) => s !== null)
                        ? sommets
                        : r.sommets.map(coord),
                });
                break;
            }
            case 'vecteur': {
                const de = o.def.type === 'vecteur' ? ref(o.def.de) : null;
                const a = o.def.type === 'vecteur' ? ref(o.def.a) : null;
                sortie.push({ type: 'vecteur', de: de ?? coord(r.de), a: a ?? coord(r.a) });
                break;
            }
            case 'fonction':
                if (o.def.type === 'fonction') {
                    sortie.push({
                        type: 'fonction',
                        famille: o.def.famille,
                        parametres: o.def.parametres,
                        ...(o.def.domaine !== undefined ? { domaine: o.def.domaine } : {}),
                    });
                }
                else
                    griefs.push('une courbe par points ne s’écrit pas dans une figure d’énoncé');
                break;
            case 'marque':
                if (o.def.type === 'angle') {
                    const s = ref(o.def.sommet);
                    const de = ref(o.def.de);
                    const a = ref(o.def.a);
                    if (s !== null && de !== null && a !== null) {
                        sortie.push({ type: 'angle', sommet: s, de, a, ...(o.def.droit ? { droit: true } : {}) });
                    }
                }
                break;
            case 'transformation':
                break;
            default:
                griefs.push(`un objet « ${o.def.type} » ne s’écrit pas dans une figure d’énoncé`);
        }
    }
    return {
        figure: {
            espace: 'plan',
            repere,
            objets: sortie,
        },
        griefs,
    };
}
/** La figure d'énoncé normalisée ; `null` : aucune, ou elle ne se dessine pas. */
export function planDuProjet(p) {
    if (p.figure.length === 0)
        return null;
    const n = normaliserFigureV2(figureDEnonceDuProjet(p.repere, p.figure).figure);
    return 'figure' in n && n.figure.espace === 'plan' ? n.figure : null;
}
/* ─────────────────────────── les cibles ─────────────────────────── */
/** Le genre de cible d'un objet résolu ; `null` : il ne se demande pas à l'élève. */
export function genreDeCible(r) {
    switch (r?.genre) {
        case 'point':
            return 'point';
        case 'ligne':
            return r.sorte === 'segment' ? 'segment' : r.sorte === 'droite' ? 'droite' : 'ligne';
        case 'cercle':
            return 'cercle';
        case 'polygone':
            return 'polygone';
        case 'vecteur':
            return 'vecteur';
        case 'fonction':
            return 'fonction';
        case 'demi_plan':
            return 'demi_plan';
        default:
            return null;
    }
}
const PAIRES_MAX = 28;
function aireDe(s) {
    let a = 0;
    for (let i = 0; i < s.length; i += 1) {
        const p = s[i];
        const q = s[(i + 1) % s.length];
        a += p.x * q.y - q.x * p.y;
    }
    return Math.abs(a) / 2;
}
function paires(l) {
    const sortie = [];
    for (let i = 0; i < l.length; i += 1) {
        for (let j = i + 1; j < l.length; j += 1)
            sortie.push([l[i], l[j]]);
    }
    return sortie.slice(0, PAIRES_MAX);
}
/** La clé d'une contrainte, sans son erreur : deux écritures de la même mesure sont la même. */
export const cleDeContrainte = (c) => {
    const { erreur: _erreur, ...reste } = c;
    return JSON.stringify(reste);
};
/**
 * Les contraintes que la construction `objets` TIENT, cible par cible —
 * chacune MESURÉE par le vérificateur du produit, jamais devinée. L'auteur
 * coche celles qui comptent. Le vocabulaire est celui du manuel
 * (`CONTRAINTES_TRACE`) ; une image n'est proposée que par une transformation
 * qui la donne vraiment (translation, réflexion d'axe une droite de l'énoncé,
 * quart et demi-tour autour d'un point de l'énoncé, homothétie de rapport
 * mesuré).
 */
export function proposerContraintes(repere, enonce, objets, cibles) {
    const doc = documentDeTrace(repere, { objets, calque: enonce !== null });
    if (griefsDocument(doc, enonce).length > 0 || cibles.length === 0)
        return [];
    const resolue = resoudreConstruction(doc, enonce);
    const objetsEnonce = enonce?.objets ?? [];
    const pointsE = objetsEnonce.flatMap((o) => o.type === 'point' && o.nom ? [{ nom: o.nom, p: { x: o.x, y: o.y } }] : []);
    const rangs = (types) => objetsEnonce.flatMap((o, i) => (types.includes(o.type) ? [`e:${i}`] : []));
    const lignesE = rangs(['segment', 'droite']);
    const cerclesE = rangs(['cercle']);
    const polygonesE = rangs(['polygone']);
    const pt = (nom) => ({ enonce: nom });
    const n = nombrePropre;
    const candidats = [];
    const ajouter = (c) => {
        candidats.push(c);
    };
    for (const cible of cibles) {
        const r = resolue.objets.get(cible.objet);
        const c = { cible: cible.id };
        const autres = cibles.filter((x) => x.id !== cible.id);
        switch (r?.genre) {
            case 'point':
                ajouter({ type: 'point_en', point: c, x: n(r.p.x), y: n(r.p.y) });
                for (const [a, b] of paires(pointsE)) {
                    ajouter({ type: 'milieu', point: c, de: pt(a.nom), a: pt(b.nom) });
                    ajouter({ type: 'equidistant', point: c, de: pt(a.nom), a: pt(b.nom) });
                }
                for (const [a, b] of paires([
                    ...lignesE.map((e) => ({ enonce: e })),
                    ...cerclesE.map((e) => ({ enonce: e })),
                    ...autres.filter((x) => x.genre !== 'point').map((x) => ({ cible: x.id })),
                ])) {
                    ajouter({ type: 'intersection', point: c, objets: [a, b] });
                }
                for (const x of autres.filter((y) => y.genre !== 'point' && y.genre !== 'vecteur')) {
                    ajouter({ type: 'sur', point: c, objet: { cible: x.id } });
                }
                break;
            case 'ligne': {
                for (const p of pointsE) {
                    ajouter({ type: 'passe_par', ligne: c, point: pt(p.nom) });
                }
                for (const x of autres.filter((y) => y.genre === 'point')) {
                    ajouter({ type: 'passe_par', ligne: c, point: { cible: x.id } });
                }
                const dx = r.b.x - r.a.x;
                if (Math.abs(dx) > 1e-9 && r.sorte !== 'segment') {
                    const pente = (r.b.y - r.a.y) / dx;
                    ajouter({ type: 'pente', ligne: c, valeur: n(pente) });
                    ajouter({
                        type: 'ordonnee_origine',
                        ligne: c,
                        valeur: n(r.a.y - pente * r.a.x),
                    });
                }
                for (const e of lignesE) {
                    ajouter({ type: 'perpendiculaire', ligne: c, a: { enonce: e } });
                    ajouter({ type: 'parallele', ligne: c, a: { enonce: e } });
                }
                for (const x of autres.filter((y) => ['ligne', 'droite', 'segment'].includes(y.genre))) {
                    ajouter({ type: 'perpendiculaire', ligne: c, a: { cible: x.id } });
                    ajouter({ type: 'parallele', ligne: c, a: { cible: x.id } });
                }
                for (const [a, b] of paires(pointsE)) {
                    ajouter({ type: 'mediatrice', ligne: c, de: pt(a.nom), a: pt(b.nom) });
                }
                for (const s of pointsE) {
                    for (const [a, b] of paires(pointsE.filter((x) => x.nom !== s.nom))) {
                        ajouter({
                            type: 'bissectrice',
                            ligne: c,
                            sommet: pt(s.nom),
                            de: pt(a.nom),
                            a: pt(b.nom),
                        });
                    }
                }
                if (r.sorte === 'segment') {
                    ajouter({
                        type: 'longueur',
                        segment: c,
                        valeur: n(Math.hypot(dx, r.b.y - r.a.y)),
                    });
                }
                break;
            }
            case 'cercle':
                ajouter({ type: 'rayon', cercle: c, valeur: n(r.rayon) });
                for (const p of pointsE) {
                    ajouter({ type: 'sur', point: pt(p.nom), objet: c });
                }
                break;
            case 'polygone': {
                const aire = aireDe(r.sommets);
                if (aire > 0)
                    ajouter({ type: 'aire', polygone: c, valeur: n(aire) });
                for (const e of polygonesE) {
                    const source = { enonce: e };
                    ajouter({ type: 'isometrique', objet: c, source });
                    const src = objetsEnonce[Number(e.slice(2))];
                    const sommetsSrc = src?.type === 'polygone' ? src.sommets : [];
                    const aireSrc = aireDe(sommetsSrc);
                    const k = aireSrc > 0 ? Math.sqrt(aire / aireSrc) : 0;
                    if (k > 0) {
                        ajouter({ type: 'semblable', objet: c, source, rapport: n(k) });
                    }
                    /* Les transformations qui la donneraient : mesurées, jamais crues. */
                    const centre = (s) => ({
                        x: s.reduce((t, p) => t + p.x, 0) / s.length,
                        y: s.reduce((t, p) => t + p.y, 0) / s.length,
                    });
                    if (sommetsSrc.length > 0) {
                        const g = centre(r.sommets);
                        const g0 = centre(sommetsSrc);
                        ajouter({
                            type: 'image',
                            objet: c,
                            source,
                            transformation: { type: 'translation', x: n(g.x - g0.x), y: n(g.y - g0.y) },
                        });
                    }
                    for (const l of lignesE) {
                        ajouter({
                            type: 'image',
                            objet: c,
                            source,
                            transformation: { type: 'reflexion', axe: { enonce: l } },
                        });
                    }
                    for (const p of pointsE) {
                        for (const angle of [90, -90, 180]) {
                            ajouter({
                                type: 'image',
                                objet: c,
                                source,
                                transformation: { type: 'rotation', centre: pt(p.nom), angle },
                            });
                        }
                        if (k > 0 && Math.abs(k - 1) > 1e-6) {
                            ajouter({
                                type: 'image',
                                objet: c,
                                source,
                                transformation: { type: 'homothetie', centre: pt(p.nom), rapport: n(k) },
                            });
                        }
                    }
                }
                break;
            }
            case 'vecteur':
                ajouter({
                    type: 'vecteur_egal',
                    objet: c,
                    x: n(r.a.x - r.de.x),
                    y: n(r.a.y - r.de.y),
                });
                break;
            case 'fonction': {
                const def = objets.find((o) => o.id === cible.objet)?.def;
                if (def?.type === 'fonction') {
                    ajouter({
                        type: 'courbe_egale',
                        objet: c,
                        famille: def.famille,
                        parametres: def.parametres,
                        ...(def.domaine !== undefined ? { domaine: [...def.domaine] } : {}),
                    });
                }
                break;
            }
            default:
                break;
        }
    }
    const corrigeCibles = cibles.map((x) => ({ id: x.id, genre: x.genre }));
    const vues = new Set();
    const sortie = [];
    for (const k of candidats) {
        const cle = cleDeContrainte(k);
        if (vues.has(cle))
            continue;
        vues.add(cle);
        let tenue = false;
        try {
            const v = verifierConstruction(doc, enonce, attenduDuTrace({ cibles: corrigeCibles, contraintes: [k] }, doc));
            tenue = v.constats.length === 1 && v.constats[0]?.etat === 'tenue';
        }
        catch {
            tenue = false;
        }
        if (!tenue)
            continue;
        const phrase = phrasesDesContraintes([k], corrigeCibles)[0]?.texte ?? k.type;
        sortie.push({ contrainte: k, phrase, cle });
    }
    return sortie;
}
/* ─────────────────────────── les outils, le contrôle ─────────────────────────── */
/** Les outils dont la construction a besoin : ceux que l'élève doit avoir sous la main. */
export function outilsDeLaConstruction(objets) {
    const outils = new Set(['point']);
    for (const o of objets) {
        const outil = OUTIL_PAR_DEFINITION[o.def.type];
        if (outil !== null && outil !== undefined && OUTILS_TRACE.includes(outil)) {
            outils.add(outil);
        }
        if (o.def.type === 'cercle_par_point' || o.def.type === 'cercle_rayon')
            outils.add('cercle');
    }
    return OUTILS_TRACE.filter((x) => outils.has(x));
}
const ecrit = (v) => {
    const x = nombrePropre(v);
    return x < 0 ? `-${String(-x)}` : String(x);
};
/**
 * Le contrôle par calcul que la chaîne d'import recalcule (`controle.attendu`,
 * syntaxe sympy de `tools/contenu/verifier-exercices.py`) : l'objet de la cible
 * récrit depuis la construction. `''` : il s'écrit à la main (une droite
 * verticale, une courbe hors des familles simples).
 */
export function controlePropose(repere, enonce, objets, cible) {
    if (cible === undefined)
        return '';
    const doc = documentDeTrace(repere, { objets, calque: enonce !== null });
    const r = resoudreConstruction(doc, enonce).objets.get(cible.objet);
    switch (r?.genre) {
        case 'point':
            return `(${ecrit(r.p.x)}, ${ecrit(r.p.y)})`;
        case 'ligne': {
            const dx = r.b.x - r.a.x;
            if (Math.abs(dx) < 1e-9)
                return '';
            if (cible.genre === 'segment') {
                return `segment((${ecrit(r.a.x)}, ${ecrit(r.a.y)}), (${ecrit(r.b.x)}, ${ecrit(r.b.y)}))`;
            }
            const a = (r.b.y - r.a.y) / dx;
            const b = r.a.y - a * r.a.x;
            const terme = nombrePropre(b) === 0 ? '' : nombrePropre(b) > 0 ? ` + ${ecrit(b)}` : ` - ${ecrit(-b)}`;
            return `${ecrit(a)}*x${terme}`;
        }
        case 'cercle':
            return `cercle((${ecrit(r.centre.x)}, ${ecrit(r.centre.y)}), ${ecrit(r.rayon)})`;
        case 'polygone':
            return `polygone(${r.sommets.map((p) => `(${ecrit(p.x)}, ${ecrit(p.y)})`).join(', ')})`;
        case 'vecteur':
            return `vecteur(${ecrit(r.a.x - r.de.x)}, ${ecrit(r.a.y - r.de.y)})`;
        default:
            return '';
    }
}
/** La description proposée : celle que l'élève qui ne voit pas entendrait (`decrireFigureV2`). */
export function descriptionProposee(p) {
    const plan = planDuProjet(p);
    return plan === null ? '' : decrireFigureV2(plan);
}
/* ─────────────────────────── l'exercice et ses griefs ─────────────────────────── */
const MOTIF_CODE = /^[A-Z][A-Z0-9]*(-[A-Z0-9]+)+$/u;
const MOTIF_ALIAS = /^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/u;
/**
 * L'exercice du projet, au format d'échange (`exercices/*.json`), et TOUT ce
 * qui l'empêcherait d'entrer : la figure qui ne se dessine pas, la solution
 * illisible ou que le vérificateur ne juge pas juste, une contrainte manquante,
 * un tracé fautif qui ne déclenche pas son erreur, un réglage ou une figure
 * dynamique mal formés, une description trop courte, les champs du format.
 * Les règles sont celles de l'import (`valider-exercices.ts`), rejouées ici
 * par les MÊMES fonctions du paquet partagé ; l'import les rejoue encore.
 */
export function exerciceDuProjet(p) {
    const griefs = [];
    const dire = (g) => {
        griefs.push(g);
    };
    const code = `${p.chapitre}-${p.alias}`;
    if (!MOTIF_CODE.test(p.chapitre))
        dire('chapitre : un code en majuscules (« MAT-3-AFF »)');
    if (!MOTIF_ALIAS.test(p.alias))
        dire('alias : en majuscules (« N1-1-01 »)');
    if (!MOTIF_ALIAS.test(p.notion))
        dire('notion : un alias du chapitre (« N1 »)');
    if (!COMPETENCES_EXERCICE.includes(p.competence))
        dire('compétence inconnue');
    if (!CATEGORIES_EXERCICE.includes(p.categorie))
        dire('catégorie inconnue');
    if (!(Number.isInteger(p.minutesEstimees) && p.minutesEstimees >= 1)) {
        dire('durée estimée : au moins une minute');
    }
    if (p.enonce.trim() === '')
        dire('énoncé : vide');
    const etapes = p.demarcheReference.filter((s) => s.ecriture.trim() !== '' || s.justification.trim() !== '');
    if (etapes.length < 2)
        dire('démarche de référence : au moins deux étapes');
    if (etapes.some((s) => s.ecriture.trim() === '' || s.justification.trim() === '')) {
        dire('démarche de référence : chaque étape a son écriture et sa justification');
    }
    const indices = p.indices.filter((i) => i.trim() !== '');
    if (indices.length !== 0 && indices.length !== 2)
        dire('indices : deux, ou aucun');
    if (indices.length > 0 && !(indices[0] ?? '').includes('?')) {
        dire('indices : le premier est une question');
    }
    /* ── La figure d'énoncé (I-8) ── */
    const avecFigure = p.figure.length > 0;
    const { figure, griefs: griefsFigure } = figureDEnonceDuProjet(p.repere, p.figure);
    griefsFigure.forEach((g) => dire(`figure : ${g}`));
    const n = avecFigure ? normaliserFigureV2(figure) : null;
    if (n !== null && 'griefs' in n)
        n.griefs.forEach((g) => dire(`figure : ${g}`));
    const plan = n !== null && 'figure' in n && n.figure.espace === 'plan' ? n.figure : null;
    const decrit = (p.decrit.trim() === '' ? descriptionProposee(p) : p.decrit).trim();
    if (avecFigure && (decrit.length < 20 || decrit.length > 900)) {
        dire('description de la figure : de 20 à 900 caractères');
    }
    const base = {
        alias: p.alias,
        code,
        version: 1,
        niveauExercice: p.niveauExercice,
        competence: p.competence,
        categorie: p.categorie,
        notions: [{ notion: p.notion, poids: 1 }],
        enonce: p.enonce.trim(),
    };
    const commun = {
        demarcheReference: etapes.map((s) => ({
            ecriture: s.ecriture.trim(),
            justification: s.justification.trim(),
            notion: p.notion,
        })),
        indices,
        minutesEstimees: p.minutesEstimees,
    };
    /* ── Déplacer et observer (L2, F2) : une démarche sur une figure dynamique ── */
    if (p.nature === 'observer') {
        if (!avecFigure)
            dire('une figure à observer a une figure d’énoncé');
        if (p.dynamique === undefined || p.dynamique.points.length === 0) {
            dire('figure dynamique : désigne au moins un point déplaçable');
        }
        else if (plan !== null) {
            griefsFigureDynamique(figure, p.dynamique).forEach((g) => dire(`figure dynamique : ${g}`));
        }
        if (p.resultatAttendu.trim() === '')
            dire('résultat attendu : vide');
        return {
            exercice: {
                ...base,
                ...(avecFigure
                    ? {
                        figure: {
                            genre: 'figure_v2',
                            decrit,
                            figure,
                            ...(p.dynamique !== undefined ? { dynamique: p.dynamique } : {}),
                        },
                    }
                    : {}),
                reponse: { nature: 'demarche', resultatAttendu: p.resultatAttendu.trim() },
                ...commun,
                reponsesFautives: [],
                controle: { attendu: p.controle.trim(), resultat: p.resultatControle.trim() },
            },
            griefs: [
                ...griefs,
                ...(p.controle.trim() === '' ? ['contrôle : le calcul de contrôle est attendu'] : []),
                ...(p.resultatControle.trim() === ''
                    ? ['contrôle : une démarche annonce un résultat, son contrôle est attendu']
                    : []),
            ],
            fautifs: [],
        };
    }
    /* ── Tracer ── */
    if (p.dynamique !== undefined)
        dire('une figure dynamique ne sert pas de calque à un tracé');
    if (p.solution.length === 0)
        dire('solution : construis-la');
    if (p.cibles.length === 0)
        dire('cibles : désigne au moins un objet de la solution');
    if (p.contraintes.length === 0)
        dire('contraintes : coche au moins une contrainte');
    if (p.outils.length === 0)
        dire('outils : offre au moins un outil');
    const ids = new Set(p.solution.map((o) => o.id));
    p.cibles.forEach((c) => {
        if (!ids.has(c.objet))
            dire(`cible « ${c.id} » : son objet n’est plus dans la solution`);
    });
    const manquants = outilsDeLaConstruction(p.solution).filter((o) => o !== 'point' && !p.outils.includes(o));
    if (manquants.length > 0) {
        dire(`outils : la solution emploie ${manquants.join(', ')}, que l’élève n’aurait pas`);
    }
    const doc = documentDeTrace(p.repere, { objets: p.solution, calque: plan !== null });
    if (p.solution.length > 0) {
        griefsDocument(doc, plan).forEach((g) => dire(`solution : ${g}`));
    }
    const contraintes = p.contraintes.map((k) => k.erreur.trim() === '' ? k.contrainte : { ...k.contrainte, erreur: k.erreur.trim() });
    p.contraintes.forEach((k) => {
        if (k.erreur.trim() !== '' && !MOTIF_ALIAS.test(k.erreur.trim())) {
            dire(`contrainte : l’erreur « ${k.erreur} » n’est pas un alias du chapitre`);
        }
    });
    const corrige = {
        nature: 'trace',
        ...(avecFigure ? {} : { repere: p.repere }),
        outils: [...p.outils],
        cibles: p.cibles.map((c) => ({ id: c.id, genre: c.genre })),
        contraintes,
        solution: p.solution.map((o) => ({ id: o.id, ...(o.nom ? { nom: o.nom } : {}), def: o.def })),
        ...(p.reglage !== undefined ? { reglage: p.reglage } : {}),
    };
    if (p.reglage !== undefined) {
        griefsDuReglage(p.reglage, corrige).forEach((g) => dire(`réglage : ${g}`));
    }
    const fautives = p.fautifs.map((f) => ({
        reponse: f.reponse.trim(),
        erreur: f.erreur.trim(),
        cheminement: f.cheminement.trim(),
        contraintes: [...f.contraintes],
    }));
    p.fautifs.forEach((f, i) => {
        if (f.reponse.trim() === '')
            dire(`tracé fautif ${i + 1} : son libellé`);
        if (!MOTIF_ALIAS.test(f.erreur.trim()))
            dire(`tracé fautif ${i + 1} : l’alias de son erreur`);
        if (f.cheminement.trim() === '')
            dire(`tracé fautif ${i + 1} : son cheminement`);
        if (f.contraintes.length === 0)
            dire(`tracé fautif ${i + 1} : coche ce qu’il tient`);
        if (f.controle.trim() === '')
            dire(`tracé fautif ${i + 1} : son contrôle par calcul`);
    });
    /* Rejouée par le JUGEMENT du serveur : la solution est juste, chaque tracé fautif déclenche son erreur. */
    const exercice = {
        corrige: {
            ...corrige,
            tracesFautifs: fautives
                .filter((f) => f.contraintes.length > 0)
                .map((f) => ({ reponse: f.reponse, contraintes: f.contraintes })),
        },
        figure: avecFigure ? { genre: 'figure_v2', decrit, figure } : undefined,
        fautives: fautives.map((f) => ({
            reponse: f.reponse,
            erreurCode: f.erreur,
            cheminement: f.cheminement,
        })),
    };
    let solutionJuste = false;
    if (p.solution.length > 0 && p.cibles.length > 0 && contraintes.length > 0) {
        try {
            const j = jugerTrace(exercice, p.solution);
            solutionJuste = j.genre === 'juge' && j.verdict === 'juste';
            if (!solutionJuste) {
                dire(`solution : le vérificateur ne la juge pas juste (${j.genre === 'juge' ? j.verdict : j.retroaction})`);
            }
        }
        catch (e) {
            dire(`solution : elle ne se juge pas (${e.message})`);
        }
    }
    const etatsFautifs = p.fautifs.map((f, i) => {
        let declenche = false;
        if (solutionJuste && f.objets.length > 0 && f.contraintes.length > 0) {
            try {
                const j = jugerTrace(exercice, f.objets);
                declenche = j.genre === 'juge' && j.fautive?.reponse === fautives[i]?.reponse;
            }
            catch {
                declenche = false;
            }
        }
        if (solutionJuste && !declenche) {
            dire(`tracé fautif ${i + 1} : rejoué, il ne déclenche pas son erreur`);
        }
        return { reponse: f.reponse, declenche };
    });
    if (p.controle.trim() === '')
        dire('contrôle : le calcul de contrôle de la solution est attendu');
    return {
        exercice: {
            ...base,
            ...(avecFigure ? { figure: { genre: 'figure_v2', decrit, figure } } : {}),
            reponse: corrige,
            ...commun,
            reponsesFautives: fautives,
            controle: {
                attendu: p.controle.trim(),
                ...(p.fautifs.length > 0 ? { fautives: p.fautifs.map((f) => f.controle.trim()) } : {}),
            },
        },
        griefs,
        fautifs: etatsFautifs,
    };
}
/** Le fichier du chapitre qui porte l'exercice : ce que la chaîne d'import lit. */
export function fichierDExercices(chapitre, exercice) {
    return { formatVersion: FORMAT_CHAPITRE_VERSION, chapitre, exercices: [exercice] };
}
//# sourceMappingURL=editeur-figure.js.map